import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  adminListBookings, adminInsuranceAudit, adminCalendar, adminCreateBlock, adminDeleteBlock,
  adminUpdateStatus, adminSettle, adminResyncFmx, adminUpdateRateCard,
  adminPendingPayouts, adminRecordPayout,
  adminGetManagedVenue,
  PIPELINE_STAGES,
  type ManagedBooking, type CalendarBlock, type RateCard,
} from '../../lib/managedVenues';
import { listVenueDirectory } from '../../lib/tenancy';
import { ApiError } from '../../lib/apiClient';
import { formatMoney, formatDateShort } from '../../lib/format';
import './AdminPages.css';

/**
 * The Vyrtual operations console.
 *
 * Admin only. This is the operator's view of other people's bookings, contact details and
 * money, which is why every endpoint behind it is requireAdmin and why none of it is
 * reachable from the public showcase.
 *
 * Four jobs, four tabs: work the queue, see the whole calendar, chase missing insurance,
 * and change prices without a deploy.
 *
 * ── TASK 7 CHANGED TWO THINGS, AND NEITHER IS A REDESIGN ─────────────────────────────
 *
 * 1. `initialTab` and the optional :venueId in the path, so the grouped Admin nav can
 *    address the calendar and the rate card directly instead of landing everyone on the
 *    queue and asking them to click. Same screen, same tabs.
 *
 * 2. The venue list now comes from GET /api/admin/venue-directory instead of the PUBLIC
 *    managed-venue endpoint. That endpoint filters is_public = TRUE, so a privately
 *    operated venue was invisible here — which is the state the only real venue in
 *    production is in, leaving the calendar's venue picker and the rate card tab empty
 *    for the operator who owns them. That was a live defect, not a cosmetic one.
 */

const TABS = ['Queue', 'Calendar', 'Insurance', 'Payouts', 'Rate card'] as const;
type Tab = typeof TABS[number];

const STATUSES = ['submitted', 'under_review', 'approved', 'tech_prep', 'live', 'settled', 'declined'];

function BookingRow({ b, onChanged }: { b: ManagedBooking; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settling, setSettling] = useState(false);
  const [count, setCount] = useState('0');

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try { await fn(); onChanged(); } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That did not work.');
    } finally { setBusy(false); }
  }

  return (
    <article className="net-row">
      <div className="net-row__main">
        <span className="net-row__name">{b.eventName}</span>
        <span className="net-row__email mono">{b.organizerName} · {b.organizerEmail}</span>
        <div className="net-row__badges">
          <span className="badge badge--muted">{b.status.replace(/_/g, ' ')}</span>
          <span className="badge badge--muted mono">
            {b.pipelineStage + 1}/6 {PIPELINE_STAGES[b.pipelineStage]}
          </span>
          {b.paymentCompletedAt
            ? <span className="badge badge--ok">Paid</span>
            : <span className="badge badge--warn">Unpaid</span>}
          {b.coiUrl
            ? <span className="badge badge--ok">COI on file</span>
            : <span className="badge badge--danger">No COI</span>}
          {b.fmxSyncedAt
            ? <span className="badge badge--ok">FMX synced</span>
            : <span className="badge badge--muted">Not synced</span>}
        </div>
        <span className="opp-reg__hint">
          {b.venueName ?? ''} · {formatDateShort(b.eventStartDate)}
          {b.eventEndDate !== b.eventStartDate ? ` to ${formatDateShort(b.eventEndDate)}` : ''}
          {typeof b.daysUntil === 'number' ? ` · ${b.daysUntil} days out` : ''}
          {' · '}{formatMoney(b.organizerCostCents)} due
        </span>
        {error && <p className="adm-error" role="alert">{error}</p>}
      </div>

      <div className="net-row__actions">
        <select className="adm-select" value={b.status} disabled={busy}
          onChange={(e) => void run(() => adminUpdateStatus(b.id, { status: e.target.value }))}>
          {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        {b.pipelineStage < 5 && (
          <button type="button" className="button button--ghost adm-btn-sm" disabled={busy}
            onClick={() => void run(() => adminUpdateStatus(b.id, { pipelineStage: b.pipelineStage + 1 }))}>
            Advance
          </button>
        )}
        <button type="button" className="button button--ghost adm-btn-sm" disabled={busy}
          onClick={() => void run(() => adminResyncFmx(b.id))}>
          Resync FMX
        </button>
        {b.paymentCompletedAt && b.status !== 'settled' && (
          <button type="button" className="button button--primary adm-btn-sm"
            onClick={() => setSettling((v) => !v)}>
            {settling ? 'Cancel' : 'Settle'}
          </button>
        )}
      </div>

      {settling && (
        <div className="net-row__panel">
          <p className="opp-reg__hint">
            {b.admissionType === 'ticketed'
              ? 'Enter the final counted admissions. Fees are deducted from gross at settlement.'
              : 'This event is admission free. Settling records completion and releases nothing.'}
          </p>
          {b.admissionType === 'ticketed' && (
            <label className="field">
              <span className="field__label">Final ticket count</span>
              <input className="field__input mono" type="number" min={0} value={count}
                onChange={(e) => setCount(e.target.value)} />
            </label>
          )}
          <button type="button" className="button button--primary" disabled={busy}
            onClick={() => void run(() => adminSettle(b.id, Number(count) || 0))}>
            Release settlement
          </button>
        </div>
      )}
    </article>
  );
}

export function AdminManagedVenues({ initialTab = 'Queue' }: { initialTab?: Tab } = {}) {
  // Present only on /admin/venues/:venueId/calendar, which scopes the calendar read.
  const { venueId: scopedVenueId } = useParams<{ venueId: string }>();
  const [tab, setTab] = useState<Tab>(initialTab);
  const [bookings, setBookings] = useState<ManagedBooking[] | null>(null);
  const [audit, setAudit] = useState<ManagedBooking[] | null>(null);
  const [blocks, setBlocks] = useState<CalendarBlock[] | null>(null);
  const [venues, setVenues] = useState<{ id: string; name: string; slug?: string }[]>([]);
  const [rate, setRate] = useState<RateCard | null>(null);
  const [payouts, setPayouts] = useState<ManagedBooking[] | null>(null);
  const [payoutRef, setPayoutRef] = useState<Record<string, string>>({});
  const [rateVenue, setRateVenue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState({ status: '', missing_coi: '' });
  const [newBlock, setNewBlock] = useState({ venueId: '', date: '', blockType: 'maintenance', title: '' });

  const load = useCallback(() => {
    const params: Record<string, string> = {};
    if (filter.status) params.status = filter.status;
    if (filter.missing_coi) params.missing_coi = 'true';
    adminListBookings(params).then(setBookings)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Could not load bookings.'));
    adminInsuranceAudit(30).then(setAudit).catch(() => setAudit([]));
    adminCalendar(scopedVenueId ? { venueId: scopedVenueId } : {})
      .then(setBlocks).catch(() => setBlocks([]));
    adminPendingPayouts().then(setPayouts).catch(() => setPayouts([]));
  }, [filter, scopedVenueId]);

  useEffect(load, [load]);

  useEffect(() => {
    // Managed venues only: the rate card and calendar blocks belong to that system.
    listVenueDirectory({ venueKind: 'managed_venue' }).then((rows) => {
      const list = rows.map((v) => ({ id: v.venueId, name: v.name }));
      setVenues(list);
      const preferred = scopedVenueId && list.some((v) => v.id === scopedVenueId)
        ? scopedVenueId
        : list[0]?.id;
      if (preferred) {
        setNewBlock((b) => ({ ...b, venueId: preferred }));
        setRateVenue(preferred);
      }
    }).catch(() => setVenues([]));
  }, [scopedVenueId]);

  // The rate card is read through the venue's public slug endpoint, which only answers
  // for a public venue. Rather than guess, the tab says so when it cannot load one.
  useEffect(() => {
    if (!rateVenue) return;
    let cancelled = false;
    adminGetManagedVenue(rateVenue)
      .then((d) => { if (!cancelled) setRate(d.rateCard); })
      .catch(() => { if (!cancelled) setRate(null); });
    return () => { cancelled = true; };
  }, [rateVenue]);


  async function saveRate(patch: Partial<RateCard>) {
    if (!rateVenue) return;
    setError(null);
    try { setRate(await adminUpdateRateCard(rateVenue, patch)); } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not update the rate card.');
    }
  }

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Managed venues</h1>
          <p className="adm-lede">
            Bookings, calendar and pricing for facilities Vyrtual operates. Organizers reach
            the intake without an account; everything here is staff only.
          </p>
        </div>
      </header>

      {error && <div className="alert alert--error" role="alert">{error}</div>}

      <div className="adm-filters">
        {TABS.map((t) => (
          <button key={t} type="button" className={`adm-tab${tab === t ? ' is-active' : ''}`}
            onClick={() => setTab(t)}>
            {t}
            {t === 'Insurance' && audit && audit.length > 0 ? ` (${audit.length})` : ''}
            {t === 'Payouts' && payouts && payouts.length > 0 ? ` (${payouts.length})` : ''}
          </button>
        ))}
      </div>

      {tab === 'Queue' && (
        <>
          <div className="adm-filters">
            <select className="adm-select" value={filter.status}
              onChange={(e) => setFilter((f) => ({ ...f, status: e.target.value }))}>
              <option value="">All statuses</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
            </select>
            <button type="button"
              className={`adm-tab${filter.missing_coi ? ' is-active' : ''}`}
              onClick={() => setFilter((f) => ({ ...f, missing_coi: f.missing_coi ? '' : 'true' }))}>
              Missing COI only
            </button>
          </div>

          {bookings == null && <p className="adm-status">Loading…</p>}
          {bookings != null && bookings.length === 0 && (
            <p className="adm-status adm-muted">No bookings match.</p>
          )}
          <div className="adm-list">
            {(bookings ?? []).map((b) => <BookingRow key={b.id} b={b} onChanged={load} />)}
          </div>
        </>
      )}

      {tab === 'Calendar' && (
        <>
          <section className="net-panel">
            <h2 className="net-panel__title">Add a manual block</h2>
            <div className="wiz-grid">
              <label className="field">
                <span className="field__label">Venue</span>
                <select className="field__input" value={newBlock.venueId}
                  onChange={(e) => setNewBlock((b) => ({ ...b, venueId: e.target.value }))}>
                  {venues.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </label>
              <label className="field">
                <span className="field__label">Date</span>
                <input className="field__input" type="date" value={newBlock.date}
                  onChange={(e) => setNewBlock((b) => ({ ...b, date: e.target.value }))} />
              </label>
              <label className="field">
                <span className="field__label">Type</span>
                <select className="field__input" value={newBlock.blockType}
                  onChange={(e) => setNewBlock((b) => ({ ...b, blockType: e.target.value }))}>
                  <option value="maintenance">Maintenance</option>
                  <option value="holiday">Holiday</option>
                  <option value="internal_event">Internal event</option>
                </select>
              </label>
              <label className="field">
                <span className="field__label">Title</span>
                <input className="field__input" value={newBlock.title}
                  onChange={(e) => setNewBlock((b) => ({ ...b, title: e.target.value }))} />
              </label>
            </div>
            <button type="button" className="button button--primary"
              disabled={!newBlock.venueId || !newBlock.date || !newBlock.title.trim()}
              onClick={() => {
                setError(null);
                adminCreateBlock(newBlock).then(() => {
                  setNewBlock((b) => ({ ...b, date: '', title: '' })); load();
                }).catch((err: unknown) =>
                  setError(err instanceof ApiError ? err.message : 'Could not add that block.'));
              }}>
              Add block
            </button>
          </section>

          <div className="adm-list">
            {(blocks ?? []).map((bl) => (
              <article className="net-row" key={bl.id}>
                <div className="net-row__main">
                  <span className="net-row__name mono">{bl.date}</span>
                  <span className="net-row__email">{bl.title}</span>
                  <div className="net-row__badges">
                    <span className="badge badge--muted">{bl.type.replace(/_/g, ' ')}</span>
                    <span className="badge badge--muted">{bl.venueName}</span>
                    {bl.organizerName && (
                      <span className="badge badge--muted">{bl.organizerName}</span>
                    )}
                  </div>
                </div>
                <div className="net-row__actions">
                  {!bl.bookingId && (
                    <button type="button" className="button button--ghost adm-btn-sm"
                      onClick={() => {
                        setError(null);
                        adminDeleteBlock(bl.id).then(load).catch((err: unknown) =>
                          setError(err instanceof ApiError ? err.message : 'Could not remove it.'));
                      }}>
                      Remove
                    </button>
                  )}
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {tab === 'Insurance' && (
        <>
          <p className="adm-lede">
            Bookings in the next 30 days with no certificate on file. Without one, access
            can be refused at the door.
          </p>
          {audit != null && audit.length === 0 && (
            <p className="adm-status adm-muted">Every upcoming booking has a certificate.</p>
          )}
          <div className="adm-list">
            {(audit ?? []).map((b) => <BookingRow key={b.id} b={b} onChanged={load} />)}
          </div>
        </>
      )}

      {tab === 'Payouts' && (
        <>
          <p className="adm-lede">
            Settled events with money owed to the organizer. Ticket proceeds are held in
            Voleto&apos;s platform balance: managed venue organizers have no Stripe Connect
            account, so there is no destination to transfer to and these are disbursed by
            hand. Record the reference once you have sent it.
          </p>
          {payouts != null && payouts.length === 0 && (
            <p className="adm-status adm-muted">Nothing owed.</p>
          )}
          <div className="adm-list">
            {(payouts ?? []).map((b) => (
              <article className="net-row" key={b.id}>
                <div className="net-row__main">
                  <span className="net-row__name">{b.eventName}</span>
                  <span className="net-row__email mono">
                    {b.organizerName} · {b.organizerEmail}
                  </span>
                  <div className="net-row__badges">
                    <span className="badge badge--warn">Awaiting disbursement</span>
                    <span className="badge badge--muted mono">
                      {formatMoney(b.netPayoutCents)}
                    </span>
                  </div>
                  <span className="opp-reg__hint">
                    {b.venueName} · settled after {formatDateShort(b.eventStartDate)}
                  </span>
                </div>
                <div className="net-row__actions">
                  <input className="field__input adm-search" placeholder="Wire reference"
                    value={payoutRef[b.id] ?? ''}
                    onChange={(e) => setPayoutRef((r) => ({ ...r, [b.id]: e.target.value }))} />
                  <button type="button" className="button button--primary adm-btn-sm"
                    disabled={(payoutRef[b.id] ?? '').trim().length < 3}
                    onClick={() => {
                      setError(null);
                      adminRecordPayout(b.id, (payoutRef[b.id] ?? '').trim())
                        .then(load)
                        .catch((err: unknown) => setError(
                          err instanceof ApiError ? err.message : 'Could not record that.'));
                    }}>
                    Mark disbursed
                  </button>
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {tab === 'Rate card' && rate && (
        <section className="net-panel">
          <h2 className="net-panel__title">Live pricing</h2>
          <p className="opp-reg__hint">
            Saving writes a new rate card and retires the current one, so bookings already
            taken keep the price they were quoted.
          </p>
          <div className="wiz-grid">
            {([
              ['baseRentalCents', 'Base rental'],
              ['extraHourRateCents', 'Extra hour'],
              ['cleaningFeeCents', 'Cleaning'],
              ['techBaseRateCents', 'Technician base'],
              ['techExtraHourRateCents', 'Technician extra hour'],
              ['ledWallPriceCents', 'LED wall'],
              ['projectorPriceCents', 'Projector'],
            ] as Array<[keyof RateCard, string]>).map(([key, label]) => (
              <label className="field" key={key}>
                <span className="field__label">{label}</span>
                <input className="field__input mono" type="number" min={0}
                  defaultValue={Number(rate[key]) / 100}
                  onBlur={(e) => void saveRate({ [key]: Math.round(Number(e.target.value) * 100) })} />
              </label>
            ))}
            <label className="field">
              <span className="field__label">Facility fee %</span>
              <input className="field__input mono" type="number" min={0} step="0.01"
                defaultValue={rate.schoolFeePct}
                onBlur={(e) => void saveRate({ schoolFeePct: Number(e.target.value) })} />
            </label>
            <label className="field">
              <span className="field__label">Vyrtual fee %</span>
              <input className="field__input mono" type="number" min={0} step="0.01"
                defaultValue={rate.vyrtualFeePct}
                onBlur={(e) => void saveRate({ vyrtualFeePct: Number(e.target.value) })} />
            </label>
          </div>
          <p className="opp-reg__hint">Values save when a field loses focus.</p>
        </section>
      )}
    </div>
  );
}
